import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import httpProvider from './HttpProvider';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const isTokenExpired = token => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(
                        c =>
                            '%' +
                            ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                    )
                    .join('')
            );

            const decodedToken = JSON.parse(jsonPayload);
            const currentDate = new Date();
            return decodedToken.exp * 1000 < currentDate.getTime();
        } catch {
            return true;
        }
    };

    const initialToken = localStorage.getItem('token');
    const validToken = initialToken && !isTokenExpired(initialToken) ? initialToken : null;

    const [token, setToken_] = useState(validToken);

    const setToken = newToken => {
        localStorage.setItem('token', newToken);
        setToken_(newToken);
    };

    const getToken = useCallback(() => {
        return token;
    }, [token]);

    const removeToken = useCallback(() => {
        localStorage.removeItem('token');
        setToken_(null);
    }, []);

    useEffect(() => {
        if (!token) {
            return;
        }

        // Authorization header attachment lives in HttpProvider.js as a
        // module-level interceptor (reads localStorage directly at request
        // time) rather than here, to avoid a mount-order race: on initial
        // render React fires a child page's data-fetch-on-mount effect
        // before this effect runs, which could send the first request(s)
        // without the header even though a valid token exists.
        const responseInterceptor = httpProvider.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    removeToken();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            httpProvider.interceptors.response.eject(responseInterceptor);
        };
    }, [token, removeToken]);

    const contextValue = useMemo(
        () => ({
            token,
            setToken,
            getToken,
            removeToken,
        }),
        [token, getToken, removeToken]
    );

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);

export default AuthProvider;
